/** `useScope()`: the global project scope and time range, read from the URL (IA §1.3). */
import { useSearch } from '@tanstack/react-router';
import { useMemo } from 'react';
import { t, tp } from './i18n';
import { formatDate } from './format';
import { useMe } from './me';
import { type GlobalSearch, joinList, list, useUpdateSearch } from './search';
import { RANGE_LABELS, type RangeKey, type ResolvedRange, resolveRange } from './timeRange';

export interface Scope {
  /** Selected project ids; empty means all projects the user may see. */
  projectIds: string[];
  allProjects: boolean;
  /** Ids to send to APIs: the selection, or undefined for "all" (the server scopes). */
  apiProjects: string[] | undefined;
  projectLabel: string;
  range: ResolvedRange;
  rangeLabel: string;
  setProjects: (ids: string[]) => void;
  setRange: (key: RangeKey, from?: string, to?: string) => void;
}

export function useScope(): Scope {
  const search = useSearch({ strict: false }) as GlobalSearch;
  const me = useMe();
  const update = useUpdateSearch();
  const tz = me.company?.timezone ?? 'Europe/Brussels';

  return useMemo(() => {
    const known = new Set(me.projects.map((p) => p.id));
    const projectIds = list(search.p).filter((id) => known.has(id));
    const range = resolveRange(search.range, search.from, search.to, tz);
    const allProjects = projectIds.length === 0 || projectIds.length === me.projects.length;
    const first = me.projects.find((p) => p.id === projectIds[0]);
    const projectLabel = allProjects
      ? t('All projects')
      : projectIds.length === 1
        ? (first?.name ?? t('1 project'))
        : tp(projectIds.length, '{count} project', '{count} projects');
    const rangeLabel =
      range.key === 'custom'
        ? `${formatDate(range.from, 'short', tz)} – ${formatDate(new Date(range.to.getTime() - 1), 'short', tz)}`
        : t(RANGE_LABELS[range.key]);
    return {
      projectIds,
      allProjects,
      apiProjects: allProjects ? undefined : projectIds,
      projectLabel,
      range,
      rangeLabel,
      setProjects: (ids: string[]) => update({ p: joinList(ids.length === me.projects.length ? [] : ids) }),
      setRange: (key: RangeKey, from?: string, to?: string) =>
        update({ range: key, from: key === 'custom' ? from : undefined, to: key === 'custom' ? to : undefined }),
    };
  }, [search.p, search.range, search.from, search.to, me.projects, tz, update]);
}
